import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AIAgentSection({ user }) {
  const [duration, setDuration] = useState(7);
  const [postsPerDay, setPostsPerDay] = useState(2);
  const [generating, setGenerating] = useState(false);
  const [calendar, setCalendar] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [activatingTrial, setActivatingTrial] = useState(false);

  const effectiveIndustry = user.current_industry || user.industry;
  const totalPosts = duration * postsPerDay;
  const maxPosts = premiumStatus?.max_posts_per_day || 2;

  // Check premium status on load
  useEffect(() => {
    const fetchPremiumStatus = async () => {
      try {
        const response = await axios.get(`${API}/users/${user.id}/premium/status`);
        setPremiumStatus(response.data);
      } catch (error) {
        console.error('Error fetching premium status:', error);
      }
    };
    fetchPremiumStatus();
  }, [user.id]);

  const handleActivateTrial = async () => {
    setActivatingTrial(true);
    try {
      const response = await axios.post(`${API}/users/${user.id}/premium/activate-trial`);
      if (response.data.status === 'trial_activated') {
        alert('🎉 Premium trial activated! You now have access to all premium features for 7 days.');
        // Refresh premium status
        const statusResponse = await axios.get(`${API}/users/${user.id}/premium/status`);
        setPremiumStatus(statusResponse.data);
        setShowTrialModal(false);
      } else if (response.data.status === 'already_premium') {
        alert('You already have premium access!');
        setShowTrialModal(false);
      } else if (response.data.status === 'trial_used') {
        alert('You have already used your free trial.');
        setShowTrialModal(false);
      }
    } catch (error) {
      console.error('Error activating trial:', error);
      alert('Failed to activate trial. Please try again.');
    } finally {
      setActivatingTrial(false);
    }
  };

  const handleGenerate = async () => {
    // Check premium status
    if (!premiumStatus?.is_premium) {
      setShowTrialModal(true);
      return;
    }

    setGenerating(true);
    setCalendar(null);
    setShowPreview(false);

    try {
      const response = await axios.post(`${API}/generate/content-calendar`, {
        user_id: user.id,
        duration: duration,
        posts_per_day: postsPerDay,
        content_types: ["tip", "fact", "quote", "howto"]
      });

      if (response.data.success) {
        setCalendar(response.data.calendar);
        setShowPreview(true);
      }
    } catch (error) {
      console.error('Error generating calendar:', error);
      if (error.response?.status === 403) {
        setShowTrialModal(true);
      } else {
        alert('Failed to generate content calendar. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  // Removed - will implement in next phase

  return (
    <div className="space-y-6">
      {/* Premium Trial Modal */}
      {showTrialModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-orange-500/50 p-8 max-w-md w-full">
            <div className="text-center">
              <div className="text-6xl mb-4">🚀</div>
              <h3 className="text-2xl font-bold accent-text mb-2">Premium Feature</h3>
              <p className="text-gray-300 mb-6">
                AI Content Agent is a premium feature. Start your <span className="text-orange-400 font-semibold">7-day free trial</span> to unlock:
              </p>
              <div className="space-y-3 text-left mb-6">
                <div className="flex items-start gap-3">
                  <span className="text-green-400">✓</span>
                  <span className="text-gray-300">Generate up to <strong className="text-white">{maxPosts} posts/day</strong></span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-green-400">✓</span>
                  <span className="text-gray-300">Automated content calendar</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-green-400">✓</span>
                  <span className="text-gray-300">Analytics dashboard</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-green-400">✓</span>
                  <span className="text-gray-300">Manage up to 5 brand kits</span>
                </div>
              </div>
              <div className="space-y-3">
                <button
                  onClick={handleActivateTrial}
                  disabled={activatingTrial}
                  className="w-full py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg font-bold hover:from-orange-600 hover:to-pink-600 transition disabled:opacity-50"
                >
                  {activatingTrial ? 'Activating...' : '🎉 Start Free Trial'}
                </button>
                <button
                  onClick={() => setShowTrialModal(false)}
                  className="w-full py-2 style={{ color: 'var(--text-muted)' }} hover:text-white transition"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white">🤖 AI Content Agent</h2>
          <p className="style={{ color: 'var(--text-muted)' }} mt-1">Let AI create and schedule your content automatically</p>
        </div>
        {premiumStatus?.is_premium && (
          <div className="bg-gradient-to-r from-orange-500/20 to-pink-500/20 border border-orange-500/50 rounded-lg px-4 py-2">
            <div className="text-orange-400 font-semibold text-sm">⭐ Premium Active</div>
            {premiumStatus.trial_end && (
              <div className="text-xs style={{ color: 'var(--text-muted)' }}">
                Trial ends: {new Date(premiumStatus.trial_end).toLocaleDateString()}
              </div>
            )}
          </div>
        )}
      </div>

      {!showPreview ? (
        /* Configuration Screen */
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 p-8 max-w-3xl mx-auto">
          <div className="space-y-6">
            {/* Duration Selection */}
            <div>
              <label className="block text-white font-medium mb-3">⏰ Content Duration</label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setDuration(7)}
                  className={`p-4 rounded-lg border-2 transition ${
                    duration === 7
                      ? 'border-orange-500 bg-orange-500/20 text-orange-400'
                      : 'border-gray-600 bg-gray-800 text-gray-300 hover:border-orange-500/50'
                  }`}
                >
                  <div className="text-2xl mb-1">📅</div>
                  <div className="font-semibold">7 Days</div>
                  <div className="text-xs style={{ color: 'var(--text-muted)' }} mt-1">One Week</div>
                </button>
                <button
                  onClick={() => setDuration(15)}
                  className={`p-4 rounded-lg border-2 transition ${
                    duration === 15
                      ? 'border-orange-500 bg-orange-500/20 text-orange-400'
                      : 'border-gray-600 bg-gray-800 text-gray-300 hover:border-orange-500/50'
                  }`}
                >
                  <div className="text-2xl mb-1">📆</div>
                  <div className="font-semibold">15 Days</div>
                  <div className="text-xs style={{ color: 'var(--text-muted)' }} mt-1">Two Weeks</div>
                </button>
              </div>
            </div>

            {/* Posts Per Day */}
            <div>
              <label className="block text-white font-medium mb-3">📝 Posts Per Day</label>
              <div className="grid grid-cols-5 gap-3">
                {[1, 2, 3, 4, 5].map((num) => (
                  <button
                    key={num}
                    onClick={() => setPostsPerDay(num)}
                    disabled={num > maxPosts}
                    className={`p-3 rounded-lg border-2 transition ${
                      postsPerDay === num
                        ? 'border-blue-500 bg-blue-500/20 text-blue-400'
                        : num > maxPosts
                        ? 'border-gray-700 bg-gray-900 text-gray-600 cursor-not-allowed'
                        : 'border-gray-600 bg-gray-800 text-gray-300 hover:border-blue-500/50'
                    }`}
                  >
                    <div className="text-xl font-bold">{num}</div>
                    <div className="text-xs style={{ color: 'var(--text-muted)' }}">Post{num > 1 ? 's' : ''}</div>
                    {num > 2 && num > maxPosts && (
                      <div className="text-xs text-orange-400 mt-1">Pro</div>
                    )}
                  </button>
                ))}
              </div>
              {maxPosts < 5 && (
                <p className="text-xs style={{ color: 'var(--text-muted)' }} mt-2">
                  ⭐ Upgrade to premium for up to 5 posts/day
                </p>
              )}
            </div>

            {/* Content Mix Info */}
            <div className="theme-card-nested border rounded-lg p-4">
              <h3 className="text-white font-medium mb-3">🎨 AI Content Strategy</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">💡 Educational Content</span>
                  <span className="text-green-400">60%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: '60%' }}></div>
                </div>
                
                <div className="flex items-center justify-between mt-3">
                  <span className="text-gray-300">💬 Engagement Posts</span>
                  <span className="text-purple-400">40%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-purple-500 h-2 rounded-full" style={{ width: '40%' }}></div>
                </div>
              </div>
              <p className="text-xs style={{ color: 'var(--text-muted)' }} mt-3">
                Mix of tips, facts, quotes, safety, cost-saving strategies, and engagement content
              </p>
            </div>

            {/* Summary */}
            <div className="bg-gradient-to-r from-orange-500/20 to-pink-500/20 border border-orange-500/30 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-medium">Total Posts to Generate:</div>
                  <div className="text-gray-300 text-sm mt-1">
                    {duration} days × {postsPerDay} post{postsPerDay > 1 ? 's' : ''}/day for {effectiveIndustry}
                  </div>
                </div>
                <div className="text-3xl font-bold text-orange-400">{totalPosts}</div>
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="w-full py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg font-bold text-lg hover:from-orange-600 hover:to-pink-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? (
                <span>✨ Generating Content Calendar...</span>
              ) : (
                <span>✨ Generate & Schedule Content</span>
              )}
            </button>
          </div>
        </div>
      ) : (
        /* Preview Calendar */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold text-white">📅 Generated Content Calendar</h3>
            <button
              onClick={() => setShowPreview(false)}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition"
            >
              ← New Calendar
            </button>
          </div>

          {/* Calendar View */}
          {calendar && calendar.map((day, dayIndex) => (
            <div key={dayIndex} className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-xl font-bold text-white">{day.day_name}</h4>
                  <p className="text-sm style={{ color: 'var(--text-muted)' }}">{new Date(day.date).toLocaleDateString()}</p>
                </div>
                <div className="text-orange-400 font-semibold">
                  {day.posts.length} post{day.posts.length > 1 ? 's' : ''}
                </div>
              </div>

              {/* Posts for this day */}
              <div className="space-y-3">
                {day.posts.map((post, postIndex) => (
                  <div key={postIndex} className="theme-card-nested border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <span className="inline-block px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-semibold rounded-full">
                        {post.label}
                      </span>
                      <span className="text-xs style={{ color: 'var(--text-muted)' }}">🕐 {post.scheduled_time}</span>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed">{post.content}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Action Buttons */}
          <div className="bg-gradient-to-r from-orange-500/20 to-pink-500/20 border border-orange-500/50 rounded-lg p-6">
            <div className="text-center">
              <p className="text-white font-semibold mb-2">
                ✨ {calendar?.length} days of content ready!
              </p>
              <p className="text-gray-300 text-sm mb-4">
                Use the "Create" tab to generate images for each post and publish them
              </p>
              <button
                onClick={() => setShowPreview(false)}
                className="px-6 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition"
              >
                📝 Create More Content
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AIAgentSection;
